/* eslint-disable no-console */
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useIsomorphicLayoutEffect } from '@react-hookz/web';

export const APP_VERSIONS = {
  LEGACY: 'LEGACY',
  WEBVIEW: 'WEBVIEW'
} as const;

export type AppVersion = (typeof APP_VERSIONS)[keyof typeof APP_VERSIONS];

interface AppDetectionResult {
  isAppView: boolean;
  isLoading: boolean;
  appVersion: AppVersion;
}

/**
 * Detects if the app is running in mobile app context and determines version
 * Supports both legacy (source-based) and new WebView detection methods
 *
 * @returns object with isAppView boolean, isLoading state, and detected app version
 */
export const useIsAppView = (): AppDetectionResult => {
  const router = useRouter();
  const [isAppView, setIsAppView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [appVersion, setAppVersion] = useState<AppVersion>(APP_VERSIONS.LEGACY);

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') {
      // SSR case
      setIsLoading(false);
      return;
    }

    // First check for new WebView detection
    const detectWebView = () => {
      if (window.ReactNativeWebView || window.FantasyFootballHubApp) {
        return true;
      }

      const userAgent = navigator.userAgent || '';
      if (
        userAgent.includes('wv') ||
        userAgent.includes('WebView') ||
        userAgent.includes('ReactNative')
      ) {
        return true;
      }

      return false;
    };

    // Check for legacy source-based detection
    const detectLegacyApp = () => {
      const urlSource = router.query.source as string;
      if (urlSource === 'inapp') {
        localStorage.setItem('userSource', urlSource);
        return true;
      }

      // Check localStorage for stored source
      const storedSource = localStorage.getItem('userSource');
      return storedSource === 'inapp';
    };

    const isWebView = detectWebView();
    const isLegacyApp = detectLegacyApp();

    if (isWebView) {
      setIsAppView(true);
      setAppVersion(APP_VERSIONS.WEBVIEW);
    } else if (isLegacyApp) {
      setIsAppView(true);
      setAppVersion(APP_VERSIONS.LEGACY);
    } else {
      // Not in any app - shouldn't happen based on your requirements
      setIsAppView(false);
      setAppVersion(APP_VERSIONS.LEGACY); // Default fallback
    }

    setIsLoading(false);
  }, [router.query.source]);

  return { isAppView, isLoading, appVersion };
};
